import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SideDrawer } from '@/components/ui/SideDrawer';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import { AppRecordAccount } from '@/components/apps/AppRecordAccount';
import { rerunAppRecord } from '@/lib/app-rerun';
import { AppRecordFiche } from '@/components/apps/AppRecordFiche';
import { AppRecordNextActions } from '@/components/apps/AppRecordNextActions';
import { AppRecordToolButtons } from '@/components/apps/AppRecordToolButtons';
import { useAppToolLinks } from '@/components/apps/useAppToolLinks';
import { AppRunResult } from '@/components/apps/AppRunResult';
import { useAppData } from '@/components/apps/useAppData';
import { useAppNextActions } from '@/components/apps/useAppNextActions';
import { useKeyShortcut } from '@/lib/use-key-shortcut';
import { nextInSession, sessionOrder } from './launch-session';
import type { AppObjectNavigationState } from '@/components/apps/AppRecordsView';
import type { AppSummary } from '@/lib/app-access';
import type { AppAssistantContextSnapshot } from '@/lib/app-assistant-context';
import { cellText, recordTitle, statusOf } from './app-records-model';

interface AppObjectDrawerProps {
  app: AppSummary;
  recordId: string;
  canManage: boolean;
  /** The columns the app's list shows; the object shows them first. */
  listColumnKeys?: string[];
  /** The chat's context for this page (the app, the object, what is visible). */
  onAssistantContext: (context: AppAssistantContextSnapshot | null) => void;
}

/**
 * One object of an app, at /apps/:key/o/:id: a link people can share, opened
 * in a drawer over the app's page. Access follows the reader's account (RLS):
 * someone without access to the app, or from another company, finds nothing.
 */
export const AppObjectDrawer = ({ app, recordId, canManage, listColumnKeys, onAssistantContext }: AppObjectDrawerProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const data = useAppData(app.id);
  const toolLinks = useAppToolLinks(app.id);
  const [copied, setCopied] = useState(false);
  const [rerun, setRerun] = useState<{ state: 'idle' | 'starting' | 'started' | 'error'; message?: string }>({ state: 'idle' });
  const list = data.list;
  const record = list?.records.find((item) => item.id === recordId) ?? null;
  const base = `/apps/${encodeURIComponent(app.key)}`;
  const navigation = location.state as AppObjectNavigationState | null;

  // Séance (21/10/2026): ?seance=<launch> goes through the launch's objects that
  // still have an open action, opened from Mon travail; its order is fixed once.
  const [searchParams] = useSearchParams();
  const seance = searchParams.get('seance');
  const open = useAppNextActions(seance ? app.flowId : null);
  const [sessionIds, setSessionIds] = useState<string[] | null>(null);
  const [sessionDone, setSessionDone] = useState(false);
  useEffect(() => {
    if (seance && !sessionIds && list && open) setSessionIds(sessionOrder(list.records, seance, open, recordId));
  }, [seance, sessionIds, list, open, recordId]);

  // « Précédent / suivant » follow the séance, else the list as the person saw it; from a shared link, the default order.
  const order = sessionIds ?? navigation?.order
    ?? list?.records.filter((item) => !item.archivedAt).map((item) => item.id) ?? [];
  const back = navigation?.fromList === true || navigation?.fromWork === true;
  const index = order.indexOf(recordId);
  // Moving between objects replaces the entry: the browser's back closes the drawer.
  const go = (id: string | null | undefined) => {
    if (id) navigate(`${base}/o/${id}${seance ? `?seance=${encodeURIComponent(seance)}` : ''}`, { replace: true, state: { ...navigation, order } satisfies AppObjectNavigationState });
  };
  const close = () => { if (back) navigate(-1); else navigate(base, { replace: true }); };
  const nextOpen = sessionIds && open ? nextInSession(sessionIds, recordId, open) : null;
  const advance = () => { if (nextOpen) go(nextOpen); else setSessionDone(true); };
  useKeyShortcut('ArrowRight', () => (seance ? advance() : go(order[index + 1])), Boolean(seance));
  useKeyShortcut('ArrowLeft', () => go(order[index - 1]), Boolean(seance) && index > 0);
  useEffect(() => { setCopied(false); setRerun({ state: 'idle' }); setSessionDone(false); }, [recordId]);

  const title = record && list ? recordTitle(record, list.collection) : null;
  const assistantContext = useMemo<AppAssistantContextSnapshot | null>(() => {
    if (!record || !list || !title) return null;
    const assignee = data.members.find((member) => member.userId === record.assigneeUserId)?.name;
    const reviews = data.reviews.filter((review) => review.recordId === record.id);
    const fields = record.computed.map((cell) => {
      const label = list.collection.columns.find((column) => column.key === cell.key)?.label ?? cell.key;
      return cellText(cell) ? `${label} : ${cellText(cell)}` : '';
    }).filter(Boolean);
    return {
      surface: 'tool',
      routePath: location.pathname,
      routeLabel: `${app.title} — ${title}`,
      visibleSummary: [
        `Application « ${app.title} », page d’un objet de sa liste « ${list.collection.title} ».`,
        `Statut : ${statusOf(list.collection, record.status).label}${assignee ? `, assignée à ${assignee}` : ''}${record.dueDate ? `, échéance ${record.dueDate}` : ''}.`,
        ...fields,
        reviews.length ? `${reviews.length} revue(s) humaine(s) enregistrée(s).` : '',
      ].filter(Boolean).join('\n').slice(0, 2000),
      visibleEntities: [title, ...record.computed.filter((cell) => cell.key === 'account').map(cellText)].filter(Boolean),
      selectedFlowId: app.flowId,
      toolTitle: app.title,
      executionId: record.lastRunId,
      executionLabel: title,
      appObject: { appId: app.id, recordId: record.id },
    };
  }, [record, list, title, data.members, data.reviews, app, location.pathname]);
  useEffect(() => { onAssistantContext(assistantContext); }, [assistantContext, onAssistantContext]);

  const actions = (
    <div className="flex shrink-0 items-center gap-1.5">
      <Button variant="secondary" size="sm" className="gap-2" onClick={() => {
        void (async () => {
          try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
          } catch {
            data.setError('Le lien n’a pas pu être copié. Copiez l’adresse de cette page.');
          }
        })();
      }}>
        <Link2 className="h-4 w-4" /><span className="hidden sm:inline">{copied ? 'Lien copié' : 'Copier le lien'}</span>
      </Button>
      {order.length > 1 && <>
        <Button variant="secondary" size="sm" aria-label="Précédent" disabled={index <= 0} onClick={() => go(order[index - 1])}><ChevronLeft className="h-4 w-4" /></Button>
        <span className="text-xs tabular-nums text-domino-inkSoft">{index >= 0 ? `${index + 1} / ${order.length}` : ''}</span>
        {seance
          ? <Button variant="secondary" size="sm" title="Raccourci : →" disabled={!nextOpen} onClick={advance}>Suivante<ChevronRight className="ml-1 h-4 w-4" /></Button>
          : <Button variant="secondary" size="sm" aria-label="Suivant" disabled={index < 0 || index >= order.length - 1} onClick={() => go(order[index + 1])}><ChevronRight className="h-4 w-4" /></Button>}
      </>}
    </div>
  );

  if (!data.loaded) {
    return (
      <SideDrawer label="Chargement de la fiche" onClose={close}>
        <SkeletonGroup label="Chargement de la fiche" className="space-y-4 px-5 py-6 lg:px-8">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </SkeletonGroup>
      </SideDrawer>
    );
  }
  if (!list || !record) {
    return (
      <SideDrawer label={app.title} title={app.title} onClose={close}>
        <div className="px-5 py-6 lg:px-8">
          <p role={data.error ? 'alert' : undefined} className="text-sm text-domino-ink">{data.error ?? 'Cette page est introuvable ou vous n’y avez pas accès.'}</p>
          <Link to={base} replace className="mt-3 inline-block text-sm text-domino-aubergine underline">Revenir à {app.title}</Link>
        </div>
      </SideDrawer>
    );
  }

  return (
    <SideDrawer label={title ?? app.title} title={title} actions={actions} onClose={close}>
      <article className="space-y-6 px-5 py-6 lg:px-8">
        {data.error && <p role="alert" className="text-sm text-[var(--domino-critical)]">{data.error}</p>}
        {seance && (sessionDone || (open && sessionIds && !nextOpen && !(recordId in open))) && (
          <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-domino-cardBorder bg-domino-lilac/40 px-4 py-3">
            <p className="text-sm text-domino-ink">Lancement traité.</p>
            <Button size="sm" onClick={() => (navigation?.fromWork ? navigate(-1) : navigate('/tasks'))}>Revenir à Mon travail</Button>
          </div>
        )}

        <AppRecordFiche key={record.id} appId={app.id} data={data} collection={list.collection} record={record} canManage={canManage} listColumnKeys={listColumnKeys}
          lead={list.collection.mode === 'runs' && record.lastRunId ? (
            <div className="space-y-3">
              {app.flowId && (
                <div className="flex flex-wrap items-center gap-3">
                  <Button variant="secondary" size="sm" disabled={rerun.state === 'starting'}
                    onClick={() => {
                      setRerun({ state: 'starting' });
                      void rerunAppRecord(app.flowId!, record)
                        .then(() => setRerun({ state: 'started', message: 'Analyse relancée.' }))
                        .catch((cause) => setRerun({ state: 'error', message: cause instanceof Error ? cause.message : 'L’analyse n’a pas pu être relancée.' }));
                    }}>
                    {rerun.state === 'starting' ? 'Relance…' : 'Relancer l’analyse'}
                  </Button>
                  {rerun.message && <p role={rerun.state === 'error' ? 'alert' : 'status'} className={rerun.state === 'error' ? 'text-sm text-[var(--domino-critical)]' : 'text-sm text-domino-inkSoft'}>{rerun.message}</p>}
                </div>
              )}
              <AppRunResult key={record.lastRunId} runId={record.lastRunId} title={title ?? app.title} />
            </div>
          ) : undefined}
          next={record.archivedAt ? undefined : <AppRecordNextActions key={record.id} recordId={record.id} session={seance ? { onEmptied: advance } : undefined} />}
          tools={<AppRecordToolButtons links={toolLinks.links} record={record} collection={list.collection} />}
          account={<AppRecordAccount recordId={record.id} refreshKey={record.lastRunId} />}
          onOpenRun={list.collection.mode === 'rows' ? (runId) => navigate(`${base}?run=${encodeURIComponent(runId)}`) : undefined}
          onAttached={(keptRecordId) => navigate(`${base}/o/${keptRecordId}`, { replace: true })} />
      </article>
    </SideDrawer>
  );
};
